import React, { useState } from 'react';
import { IBusinessPartnerTabProps } from '../..';
import { useLazyQuery } from '@apollo/client';
import { getBPVignetteRangeList } from './queries';
import { IListingData } from '@models/listing';
import {
  ActionTarget,
  IEnhancedMenuItem,
  IFilterModel,
} from '@components/table/types';
import { mapToVignetteRangeListingData } from './utils';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { vignetteRangeHeaders } from './content';
import VignetteRangeDrawer from './vignette-range-drawer/VignetteRangeDrawer';
import { IVignetteRangesTableData } from '.';
import { CDN_URL } from '@utils';

const VignetteRangeTab: React.FC<IBusinessPartnerTabProps> = ({
  generalData,
}) => {
  const [vignetteRangeDrawer, setVignetteRangeDrawer] = useState<{
    isOpen: boolean;
    selectedId: string;
  }>({
    isOpen: false,
    selectedId: null,
  });

  const [getBPVignetteRangeLazy, { loading }] = useLazyQuery(
    getBPVignetteRangeList
  );

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: false,
    orderBy: 'salesforceManagement_BusinessPartnerVignetteRange_RangeCode',
    searchKey: null,
  });

  const [tableData, setTableData] = useState<
    IListingData<IVignetteRangesTableData>
  >({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const vignetteRangeActions: IEnhancedMenuItem[] = [
    {
      name: 'new',
      title: 'New',
      isPrimary: true,
      onClick: () => {
        setVignetteRangeDrawer({
          isOpen: true,
          selectedId: null,
        });
      },
      target: ActionTarget.Entity,
      rowIndependant: true,
      supportBulk: true,
      iconUri: CDN_URL + '/icons/add-primary-red.svg',
    },
    {
      name: 'edit',
      title: 'Edit',
      isPrimary: true,
      target: ActionTarget.Entity,
      rowIndependant: false,
      onClick: (payload: string[]) => {
        setVignetteRangeDrawer({
          isOpen: true,
          selectedId: payload[0],
        });
      },
      iconUri: CDN_URL + '/icons/edit-secondary.svg',
    },
  ];

  const getListingData = async (filter = filterValues) => {
    if (!filter) {
      return {};
    }

    return getBPVignetteRangeLazy({
      variables: {
        pagination: filter.pagination,
        selectedBusinessPartnerID: generalData.businessPartner.id,
        Attribute: filter.orderBy,
        Descending: filter.descending,
        keywordSearch: filter.searchKey,
      },
      errorPolicy: 'all',
    })
      .then(({ data }) => {
        if (data) {
          const tableData = mapToVignetteRangeListingData(data);

          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  function handlePageChange(filterModel: IFilterModel): Promise<void> {
    return new Promise<void>(() => getListingData(filterModel));
  }

  return (
    <>
      <ListingWidget
        title=""
        inlineTitle="Vignette Range Listing"
        name="vignetteRangeListing"
        defaultOrderByColumn="name"
        data={tableData}
        tableSettings={{
          headers: vignetteRangeHeaders,
        }}
        loading={loading}
        orderByAscendingByDefault
        inline
        usePagination
        actions={
          generalData.businessPartner.isActive && generalData.userIsAdmin
            ? vignetteRangeActions
            : []
        }
        onPageChange={(filterModel) => {
          setFilterValues(filterModel);
          return handlePageChange(filterModel);
        }}
      />
      {vignetteRangeDrawer.isOpen && (
        <VignetteRangeDrawer
          open={vignetteRangeDrawer.isOpen}
          onClose={() =>
            setVignetteRangeDrawer({
              isOpen: false,
              selectedId: null,
            })
          }
          onSuccess={() => {
            getListingData();
          }}
          vignetteRangeId={vignetteRangeDrawer.selectedId}
        />
      )}
    </>
  );
};

export default VignetteRangeTab;
